import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavItem } from '../types';
import './Header.css';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems: NavItem[] = [
    { id: 'home', label: 'Home', href: '#home' },
    { id: 'ai-news-agg', label: 'AI News Aggregator', href: '#ai-news-agg' },
    {
      id: 'benchmarks',
      label: 'AI Egg Index',
      href: '#benchmarks',
      subItems: [
        { id: 'rankings', label: 'Rankings' },
        { id: 'about', label: 'About' },
        { id: 'details', label: 'Details' },
        { id: 'history', label: 'History' },
      ],
    },
    { id: 'demos', label: 'Demos', href: '#demos' },
    { id: 'wiki', label: 'Wiki', href: '#wiki' },
  ];

  // Close the open dropdown when clicking outside of it.
  useEffect(() => {
    if (!openDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, itemId: string) => {
    e.preventDefault();
    closeMenu();

    // Special handling for dedicated pages
    if (itemId === 'ai-news-agg') {
      navigate('/ai-news-aggregator');
      return;
    }

    if (itemId === 'demos') {
      navigate('/demos');
      return;
    }

    if (itemId === 'benchmarks') {
      navigate('/benchmarks');
      return;
    }

    if (itemId === 'wiki') {
      navigate('/wiki');
      return;
    }

    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Already on home page, just scroll
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleSubItemClick = (view: string) => {
    closeMenu();
    navigate(`/benchmarks?view=${view}`);
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    closeMenu();

    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header className="header">
      <div className="header-container">
        <div className="logo">
          <a href="#home" onClick={handleLogoClick}>Chris Austin</a>
        </div>

        <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
          <ul className="nav-list">
            {navItems.map((item) => (
              item.subItems ? (
                <li
                  key={item.id}
                  className="nav-item nav-dropdown"
                  ref={dropdownRef}
                >
                  <button
                    type="button"
                    className="nav-link nav-dropdown-toggle"
                    aria-haspopup="true"
                    aria-expanded={openDropdown === item.id}
                    onClick={() =>
                      setOpenDropdown((cur) => (cur === item.id ? null : item.id))
                    }
                  >
                    {item.label} <span className="nav-caret-sm">▾</span>
                  </button>
                  {openDropdown === item.id && (
                    <ul className="nav-dropdown-menu" role="menu">
                      {item.subItems.map((sub) => (
                        <li key={sub.id} role="none">
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => handleSubItemClick(sub.id)}
                          >
                            {sub.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ) : (
                <li key={item.id} className="nav-item">
                  <a
                    href={item.href}
                    className="nav-link"
                    onClick={(e) => handleNavClick(e, item.href, item.id)}
                  >
                    {item.label}
                  </a>
                </li>
              )
            ))}
          </ul>
        </nav>

        <button
          className={`hamburger ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  );
};

export default Header;
